import { useCallback, useReducer } from 'react'
import effects from 'app/effects/effects'

import moment from 'moment'
import MessageAlert from 'app/components/MessageAlert'
import { useIntl } from 'react-intl'
import { useDispatch } from 'react-redux'
import debounce from 'lodash.debounce'
import { isEmptyOrNil } from 'libs/empty'

export const PAGE_LIMIT = 20
const TIMEOUT_SEARCH = 1000

const actionType = {
  START_FETCH: 'START_FETCH',
  FETCH_SUCCESS: 'FETCH_SUCCESS',
  FETCH_ERROR: 'FETCH_ERROR',
  ON_SELECT_SUB_GROUP: 'ON_SELECT_SUB_GROUP',
  SET_SEARCH_TEXT: 'SET_SEARCH_TEXT',
  SET_LOADING: 'SET_LOADING',
}

let controller = new AbortController()

const initialState = {
  meta: { currentPage: 1, perPage: PAGE_LIMIT },
  params: {},
  isEmptySubscribers: true,
  subscribers: [],
  isLoading: false,
  error: undefined,
  updateAt: undefined,
  searchText: '',
}

const reducer = (state, action) => {
  switch (action.type) {
    case actionType.START_FETCH:
      return {
        ...state,
        isLoading: true,
        updateAt: moment().format(),
      }

    case actionType.FETCH_SUCCESS:
      return {
        ...state,
        isLoading: false,
        subscribers: action.payload,
        meta: action.meta,
        updateAt: moment().format(),
        isEmptySubscribers:
          isEmptyOrNil(action.params.keyword) && action.payload.length === 0,
        params: action.params,
      }
    case actionType.FETCH_ERROR:
      return {
        ...state,
        isLoading: false,
        subscribers: [],
        meta: {},
        error: action.payload,
        isEmptySubscribers: false,
        updateAt: moment().format(),
      }
    case actionType.SET_SEARCH_TEXT:
      return {
        ...state,
        searchText: action.payload,
      }

    case actionType.SET_LOADING:
      return {
        ...state,
        isLoading: true,
      }

    default:
      return state
  }
}

export default function useGroupSubscriber() {
  const [state, dispatch] = useReducer(reducer, initialState)
  const { formatMessage } = useIntl()
  const dispatchSystem = useDispatch()

  const fetchSubscriberForGroup = useCallback(
    async (params = initialState.params) => {
      controller.abort()
      controller = new AbortController()
      await effects.apis
        .getSubscriberForGroup(params, {
          signal: controller.signal,
        })
        .then((response) => {
          dispatch({
            type: 'FETCH_SUCCESS',
            payload: response.data,
            meta: response.meta,
            params,
          })
        })
        .catch((error) => {
          if (error.code !== 'ERR_CANCELED') {
            dispatchSystem(
              MessageAlert.showError({
                message: formatMessage({
                  id: 'message.alert.error',
                }),
              }),
            )
          }
        })
    },
    [],
  )

  const handleFetchSubscribersForGroup = useCallback(
    async (params) => {
      dispatch({ type: 'START_FETCH', value: true })
      await fetchSubscriberForGroup(params)
    },
    [fetchSubscriberForGroup],
  )

  const onSearchSubscriber = useCallback((params, onClearData) => {
    dispatch({ type: 'START_FETCH', value: true })
    debouncedSearch(
      {
        ...params,
        page: initialState.meta.currentPage,
        limit: initialState.meta.perPage,
      },
      onClearData,
    )
  }, [])

  const debouncedSearch = useCallback(
    debounce(async (params, onClearData) => {
      await fetchSubscriberForGroup(params)
      await onClearData()
    }, TIMEOUT_SEARCH),
    [],
  )

  const onSearchText = (text) => {
    dispatch({ type: actionType.SET_SEARCH_TEXT, payload: text })
  }

  const setSubscriberLoading = () => {
    dispatch({ type: actionType.SET_LOADING })
  }

  return {
    state,
    handleFetchSubscribersForGroup,
    onSearchSubscriber,
    onSearchText,
    setSubscriberLoading,
  }
}
